import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router'
import { Badge, ORDER_TONE } from '../components/badge'
import { DebouncedInput, FilterField, Pager, fieldClass, useListParams } from '../components/list'
import { Button, Card, ErrorNote } from '../components/ui'
import { ApiError } from '../lib/api'
import { customerName, type OrderStatus } from '../lib/customers-api'
import { formatJalaliDateTime, formatMobile, formatToman, toPersianDigits } from '../lib/fa'
import { listOrders, ORDER_STATUS_LABEL, type OrderListItemDto } from '../lib/sales-api'

const messageOf = (error: unknown): string =>
  error instanceof ApiError ? error.messageFa : 'مشکلی پیش آمد. دوباره تلاش کنید.'

/**
 * Every customer's orders, newest first. Filters live in the URL (`?q=&status=&from=&to=
 * &paidWithoutPolicy=&page=`). Orders that were paid for but have no policy are highlighted:
 * the customer's money is in and they have nothing to show for it.
 */
export function OrdersPage() {
  const { params, page, get, setFilter, goTo, clear } = useListParams()
  const location = useLocation()
  const from = `${location.pathname}${location.search}`

  const orders = useQuery({
    queryKey: ['orders', params.toString()],
    queryFn: () => listOrders(params),
    placeholderData: keepPreviousData,
  })

  return (
    <div className="max-w-7xl">
      <h1 className="mb-1 text-2xl font-bold">سفارش‌ها</h1>
      <p className="mb-6 text-sm text-muted">
        جست‌وجو با شماره سفارش، موبایل یا نام مشتری، یا پلاک (مثلاً ۱۲ ب ۳۴۵ ۱۰). این بخش فقط برای
        دیدن است.
      </p>

      <Card className="flex flex-wrap items-end gap-3">
        <FilterField label="جست‌وجو">
          <DebouncedInput
            type="search"
            autoFocus
            className="w-72"
            value={get('q')}
            onCommit={(v) => setFilter('q', v)}
            placeholder="شماره سفارش، ۰۹۱۲…، پلاک"
          />
        </FilterField>
        <FilterField label="وضعیت">
          <select
            className={`${fieldClass} w-44`}
            value={get('status')}
            onChange={(e) => setFilter('status', e.target.value)}
          >
            <option value="">همه</option>
            {(Object.keys(ORDER_STATUS_LABEL) as OrderStatus[]).map((status) => (
              <option key={status} value={status}>
                {ORDER_STATUS_LABEL[status]}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="از">
          <input
            type="date"
            className={fieldClass}
            value={get('from')}
            onChange={(e) => setFilter('from', e.target.value)}
          />
        </FilterField>
        <FilterField label="تا">
          <input
            type="date"
            className={fieldClass}
            value={get('to')}
            onChange={(e) => setFilter('to', e.target.value)}
          />
        </FilterField>
        <label className="flex h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-4 accent-amber-600"
            checked={get('paidWithoutPolicy') === 'true'}
            onChange={(e) => setFilter('paidWithoutPolicy', e.target.checked ? 'true' : undefined)}
          />
          فقط پرداخت‌شده بدون بیمه‌نامه
        </label>
        <Button variant="secondary" onClick={clear}>
          پاک‌کردن فیلترها
        </Button>
      </Card>

      <Card className="mt-6 overflow-x-auto p-0">
        {orders.isPending ? (
          <p className="p-5 text-sm text-muted">در حال بارگذاری…</p>
        ) : orders.isError ? (
          <div className="p-5">
            <ErrorNote>{messageOf(orders.error)}</ErrorNote>
          </div>
        ) : orders.data.items.length === 0 ? (
          <p className="p-5 text-sm text-muted">سفارشی با این فیلترها پیدا نشد.</p>
        ) : (
          <table className={`w-full text-sm ${orders.isPlaceholderData ? 'opacity-60' : ''}`}>
            <thead className="border-b border-line text-right text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">زمان</th>
                <th className="px-4 py-3 font-medium">مشتری</th>
                <th className="px-4 py-3 font-medium">محصول</th>
                <th className="px-4 py-3 font-medium">پلاک</th>
                <th className="px-4 py-3 font-medium">مبلغ</th>
                <th className="px-4 py-3 font-medium">وضعیت</th>
                <th className="px-4 py-3 font-medium">بیمه‌نامه</th>
                <th className="px-4 py-3 font-medium">شماره سفارش</th>
              </tr>
            </thead>
            <tbody>
              {orders.data.items.map((order) => (
                <OrderRow key={order.id} order={order} from={from} />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {orders.data ? (
        <Pager
          page={page}
          total={orders.data.total}
          pageSize={orders.data.pageSize}
          noun="سفارش"
          busy={orders.isFetching}
          onGo={goTo}
        />
      ) : null}
    </div>
  )
}

function OrderRow({ order, from }: { order: OrderListItemDto; from: string }) {
  const name = customerName(order.customer)
  return (
    <tr
      className={`border-b border-line align-top last:border-0 ${
        order.paidWithoutPolicy ? 'bg-amber-50 dark:bg-amber-950/40' : ''
      }`}
    >
      <td className="whitespace-nowrap px-4 py-3 text-muted">
        {formatJalaliDateTime(order.createdAt)}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/customers/${order.customer.id}`}
          state={{ from }}
          className="font-medium hover:text-brand-600"
        >
          {name ?? <span className="text-muted">بدون نام</span>}
        </Link>
        <div
          className="whitespace-nowrap text-xs text-muted"
          dir="ltr"
          style={{ textAlign: 'right' }}
        >
          {formatMobile(order.customer.mobile)}
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        <Link
          to={`/orders/${order.id}`}
          state={{ from }}
          className="font-medium hover:text-brand-600"
        >
          {order.productTitleFa}
        </Link>
        <div className="text-xs text-muted">{order.insurerName}</div>
      </td>
      <td className="whitespace-nowrap px-4 py-3">
        {order.plateFa ?? <span className="text-muted">—</span>}
      </td>
      <td className="whitespace-nowrap px-4 py-3">{formatToman(order.amount)}</td>
      <td className="px-4 py-3">
        <Badge tone={ORDER_TONE[order.status]}>{order.statusFa}</Badge>
        {order.paymentCount > 1 ? (
          <div className="mt-1 text-xs text-muted">
            {toPersianDigits(order.paymentCount)} تلاش پرداخت
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        {order.policy ? (
          <span dir="ltr">{toPersianDigits(order.policy.policyNumber)}</span>
        ) : order.paidWithoutPolicy ? (
          <Badge tone="warn">پرداخت‌شده، بدون بیمه‌نامه</Badge>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td
        className="px-4 py-3 font-mono text-xs text-muted"
        dir="ltr"
        style={{ textAlign: 'right' }}
      >
        <Link to={`/orders/${order.id}`} state={{ from }} className="hover:text-brand-600">
          {order.id}
        </Link>
      </td>
    </tr>
  )
}
